import VacancyLocation from './VacancyLocation'
import VacancyRole from './VacancyRole'
import cloneDeep from 'lodash/cloneDeep'
import get from 'lodash/get'
import unescape from 'lodash/unescape'
import { ModerationStages } from '@/constants/backend/ModerationStages'
import { CustomerTypes } from '@/constants/user/roles'
import { WorkExperience } from '@/constants/vacancies/experiences'
import { PositionType } from '@/constants/vacancies/positionTypes'
import { Stages, Statuses } from '@/constants/vacancies/statuses'
import { VacancyApplicationStatuses } from '@/constants/vacancies/vacancyApplicationStatuses'
import Skill from '@/models-ts/Skill'
import Avatar from '@/models-ts/user/Avatar'
import { parseSlug } from '@/utils-ts/parser'
import { stripDescriptionTags } from '@/utils-ts/strings'

interface VacancyUserItem {
  id?: string | number
  type?: string | number
  name?: string
  avatar?: LX.Avatar | LX.Avatar[]
  is_customer_verified?: LX.bool | '0' | '1' | null | boolean
  profile?: {
    type?: CustomerTypes
  }
}
export default class MyVacancyListItem {
  id: string | number
  stage: Stages
  moderationStage: ModerationStages
  status: Statuses
  user?: VacancyUserItem | null
  slug: string
  name: string
  created_at: string
  updated_at: string
  published_at?: string
  description: string
  locations: VacancyLocation[]
  payments_is_crypto: number
  position_office: boolean
  position_remote: boolean
  position_type: PositionType
  primaryRoleName: VacancyRole['name']
  primaryRole: VacancyRole
  salary_type: number
  salary_from: number
  salary_to: number
  work_experience: WorkExperience
  skills: Array<Skill>
  applicationsCount: number
  applicationsCountNew: number
  is_featured: boolean

  constructor(data: Partial<MyVacancyListItem>) {
    Object.assign(
      this,
      cloneDeep({
        id: data.id,
        stage: data.stage,
        moderationStage: data.moderationStage,
        status: data.status,
        user: data.user,
        slug: data.slug,
        is_featured: data.is_featured,
        name: unescape(data.name),
        locations: data.locations,
        description: data.description,
        created_at: data.created_at,
        updated_at: data.updated_at,
        payments_is_crypto: data.payments_is_crypto,
        published_at: data.published_at,
        position_office: data.position_office,
        position_remote: data.position_remote,
        position_type: data.position_type,
        primaryRoleName: data.primaryRoleName,
        primaryRole: data.primaryRole,
        salary_type: data.salary_type,
        salary_to: data.salary_to,
        salary_from: data.salary_from,
        skills: data.skills,
        work_experience: data.work_experience,
        applicationsCount: data.applicationsCount,
        applicationsCountNew: data.applicationsCountNew,
      })
    )
  }

  static fromServer(data: LX.MyVacancyListItem) {
    const apps = (data.relations.Application || []).filter((item) =>
      [VacancyApplicationStatuses.NEW, VacancyApplicationStatuses.IN_PROGRESS].includes(item.status)
    )
    const primaryRole = get(data.relations, 'PrimaryRole[0]', { id: null, name: null })
    return new MyVacancyListItem({
      ...data,
      stage: data.stage,
      moderationStage: data.moderation_stage || ModerationStages.PREMODERATION,
      status: data.status,
      user: {
        id: data?.user?.id,
        type: data?.user?.type,
        name: data?.user?.name,
        // @ts-ignore
        avatar: Avatar.fromServer(data?.user?.avatar),
        is_customer_verified: Number(data?.user?.is_customer_verified) === 1,
        profile: {
          type: data?.user?.profile?.type,
        },
      },
      locations: (data.relations.Location || []).map(VacancyLocation.fromServer),
      is_featured: Number(data.is_featured) === 1,
      slug: parseSlug(data.slug),
      description: stripDescriptionTags(data.description, { stripLinks: true }),
      primaryRoleName: primaryRole.name,
      primaryRole: VacancyRole.fromServer(primaryRole),
      position_office: Boolean(Number(data.position_office)),
      position_remote: Boolean(Number(data.position_remote)),
      published_at: data.first_published_at,
      salary_type: Number(data.salary_type),
      salary_from: Number(data.salary_from || 0),
      salary_to: Number(data.salary_to || 0),
      skills: (data.relations.Skill || []).map(Skill.fromServer),
      applicationsCount: apps.length,
      applicationsCountNew: apps.filter((item) => !item.is_read).length,
    })
  }

  get hasCompensation() {
    return this.salary_from > 0 || this.salary_to > 0
  }

  get remoteInfo() {
    if (this.position_remote) {
      return this.position_office ? 'Hybrid' : 'Remote'
    }
    return 'Office'
  }

  get sortField() {
    return this.updated_at || this.created_at
  }
}
